import { useState, useEffect } from 'react';
  const perms = detectPerms();
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Eye, Search, RotateCcw, Loader2, AlertCircle, X, Filter } from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';
import { ProductAutocomplete } from './forms/ProductAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DebitNotes = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [fVendorId, setFVendorId] = useState('');
  const [fCompany, setFCompany] = useState('');
  const [fCompanyInput, setFCompanyInput] = useState('');
  const [showCompanySuggest, setShowCompanySuggest] = useState(false);
  const [fDateFrom, setFDateFrom] = useState('');
  const [fDateTo, setFDateTo] = useState('');
  const [fActivePreset, setFActivePreset] = useState('');
  const [applied, setApplied] = useState({ vendorId: '', company: '', dateFrom: '', dateTo: '' });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchNotes(); }, []);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const [notesRes, prodsRes, vRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/debit-notes`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/products`, getAuthHeaders()),
        axios.get(`${BACKEND_URL}/api/vendors`, getAuthHeaders()),
      ]);
      setNotes(notesRes.data); setVendors(vRes.data || []);
      const map = {}; (prodsRes.data || []).forEach(p => { map[p.id] = p.name; }); setProductMap(map);
    } catch (err) { setError('Failed to fetch debit notes'); } finally { setLoading(false); }
  };

  const companyNames = [...new Set(notes.map(n => n.company_name).filter(Boolean))].sort();
  const companySuggestions = fCompanyInput.trim() ? companyNames.filter(n => n.toLowerCase().includes(fCompanyInput.toLowerCase())) : companyNames.slice(0, 8);

  function calcPreset(preset) {
    const now = new Date(); let fd = '', td = '';
    const pad = n => String(n).padStart(2, '0');
    const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    if (preset === 'this_month') { fd = fmt(new Date(now.getFullYear(), now.getMonth(), 1)); td = fmt(now); }
    else if (preset === 'last_month') { fd = fmt(new Date(now.getFullYear(), now.getMonth()-1, 1)); td = fmt(new Date(now.getFullYear(), now.getMonth(), 0)); }
    else if (preset === 'this_quarter') { const qs = Math.floor(now.getMonth()/3)*3; fd = fmt(new Date(now.getFullYear(), qs, 1)); td = fmt(now); }
    else if (preset === 'last_quarter') { const qs = Math.floor(now.getMonth()/3)*3-3; const y = qs<0?now.getFullYear()-1:now.getFullYear(); const qss=((qs%12)+12)%12; fd=fmt(new Date(y,qss,1)); td=fmt(new Date(y,qss+3,0)); }
    else if (preset === 'this_year') { fd = fmt(new Date(now.getFullYear(), 0, 1)); td = fmt(now); }
    else if (preset === 'last_year') { fd = fmt(new Date(now.getFullYear()-1, 0, 1)); td = fmt(new Date(now.getFullYear()-1, 11, 31)); }
    return { fd, td };
  }
  const applyPreset = (preset) => { const { fd, td } = calcPreset(preset); setFActivePreset(preset); setFDateFrom(fd); setFDateTo(td); };
  const applyFilters = () => setApplied({ vendorId: fVendorId, company: fCompany, dateFrom: fDateFrom, dateTo: fDateTo });
  const clearFilters = () => { setFVendorId(''); setFCompany(''); setFCompanyInput(''); setFDateFrom(''); setFDateTo(''); setFActivePreset(''); setApplied({ vendorId: '', company: '', dateFrom: '', dateTo: '' }); };
  const hasFilters = applied.vendorId || applied.company || applied.dateFrom || applied.dateTo;

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/debit-notes/${id}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchNotes();
    } catch (err) {
      setError('Failed to delete debit note');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const filtered = (() => {
    let r = notes.filter(n => (n.return_number || '').toLowerCase().includes(searchQuery.toLowerCase()) || (n.vendor_name || '').toLowerCase().includes(searchQuery.toLowerCase()) || (n.bill_number || '').toLowerCase().includes(searchQuery.toLowerCase()));
    if (applied.vendorId) r = r.filter(n => n.vendor_id === applied.vendorId);
    if (applied.company) r = r.filter(n => n.company_name === applied.company);
    if (applied.dateFrom) r = r.filter(n => { const d = (n.date || n.created_at || '').split('T')[0]; return d >= applied.dateFrom; });
    if (applied.dateTo) r = r.filter(n => { const d = (n.date || n.created_at || '').split('T')[0]; return d <= applied.dateTo; });
    return r;
  })();

  return (
    <div data-testid="debit-notes-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Purchase Return / Debit Notes</h1>
          <p className="text-slate-600 mt-1">Manage purchase returns and debit notes</p>
        </div>
        {perms.canAdd && (
        <button onClick={() => navigate('/admin/purchase/return/new')} data-testid="create-note-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
          <Plus className="h-5 w-5" /> Create Debit Note
        </button>
        )}
      </div>

      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search by return no., bill no., or vendor..."
            value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3"><Filter className="h-4 w-4 text-slate-500" /><span className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Filters</span>{hasFilters && <button onClick={clearFilters} data-testid="clear-filters-btn" className="ml-auto text-xs text-red-500 hover:text-red-600 font-medium">Clear All</button>}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 items-end">
          <div><label className="block text-xs font-medium text-slate-500 uppercase mb-1">Vendor</label><ProductAutocomplete value={fVendorId} onChange={setFVendorId} products={vendors} testId="filter-vendor" placeholder="Type vendor..." /></div>
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Company</label>
            <div className="relative">
              <input type="text" value={fCompanyInput} onChange={e => { setFCompanyInput(e.target.value); setFCompany(''); setShowCompanySuggest(true); }} onFocus={() => setShowCompanySuggest(true)} onBlur={() => setTimeout(() => setShowCompanySuggest(false), 150)} placeholder="Type company..." data-testid="filter-company-input" className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
              {fCompanyInput && <button onClick={() => { setFCompany(''); setFCompanyInput(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>}
              {showCompanySuggest && companySuggestions.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-0.5 bg-white border border-slate-200 rounded-sm shadow-lg max-h-44 overflow-auto">
                  {companySuggestions.map(name => (
                    <button key={name} onMouseDown={() => { setFCompany(name); setFCompanyInput(name); setShowCompanySuggest(false); }} className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 hover:text-orange-700 ${fCompany === name ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-700'}`}>{name}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div><button onClick={applyFilters} data-testid="apply-filters-btn" className="w-full px-4 py-2 bg-orange-500 text-white text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors">Apply</button></div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Date</p>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-1.5">
              {[{ key: 'this_month', label: 'This Month' }, { key: 'last_month', label: 'Last Month' }, { key: 'this_quarter', label: 'This Quarter' }, { key: 'last_quarter', label: 'Last Quarter' }, { key: 'this_year', label: 'This Year' }, { key: 'last_year', label: 'Last Year' }].map(p => (
                <button key={p.key} onClick={() => applyPreset(p.key)} data-testid={`preset-${p.key}`} className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${fActivePreset === p.key ? 'bg-orange-500 text-white border-orange-500' : 'border-slate-200 text-slate-600 hover:border-orange-400 hover:text-orange-600'}`}>{p.label}</button>
              ))}
            </div>
            <div className="h-5 w-px bg-slate-200 hidden md:block" />
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5"><label className="text-xs text-slate-500 font-medium whitespace-nowrap">From</label><input type="date" value={fDateFrom} onChange={e => { setFDateFrom(e.target.value); setFActivePreset(''); }} data-testid="date-from" className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" /></div>
              <div className="flex items-center gap-1.5"><label className="text-xs text-slate-500 font-medium">To</label><input type="date" value={fDateTo} onChange={e => { setFDateTo(e.target.value); setFActivePreset(''); }} data-testid="date-to" className="px-2 py-1.5 border border-slate-200 rounded-sm text-xs focus:border-orange-500 focus:outline-none" /></div>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <RotateCcw className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No debit notes yet</h3>
            <p className="text-slate-500 mb-6">Create your first debit note</p>
            <button onClick={() => navigate('/admin/purchase/return/new')}
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors">
              <Plus className="h-5 w-5" /> Create First Debit Note
            </button>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-8 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Return No.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Bill No.</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Vendor</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Bill Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Total</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filtered.map((note) => (
              <div key={note.id} className="border-b border-slate-100 hover:bg-slate-50">
                <div data-testid={`note-row-${note.id}`}
                  className="grid grid-cols-1 md:grid-cols-8 gap-4 px-6 pt-4 items-center">
                  <div className="text-slate-600 text-sm">{formatDate(note.date || note.created_at)}</div>
                  <div className="col-span-2 font-mono font-medium text-slate-900">{note.return_number || '-'}</div>
                  <div className="font-mono text-sm text-slate-600">{note.bill_number || '-'}</div>
                  <div className="text-slate-700 text-sm">{note.vendor_name || '-'}</div>
                  <div className="text-slate-600 text-sm">{formatDate(note.bill_date)}</div>
                  <div className="font-medium text-slate-900">₹{note.total?.toLocaleString()}</div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigate(`/admin/purchase/return/${note.id}`)} data-testid={`view-btn-${note.id}`}
                      className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors">
                      <Eye className="h-4 w-4" />
                    </button>
                    {perms.canDelete && (
                    <button onClick={() => setDeleteConfirm(note.id)} data-testid={`delete-btn-${note.id}`}
                      className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this debit note?</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50">Cancel</button>
              {perms.canDelete && (
              <button onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-btn" className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600">Delete</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
